/**
 * Safe Environment Variable Access
 * 
 * Provides typed, safe access to environment variables with build-time validation.
 * Never exposes secrets - only checks presence and provides safe defaults.
 * 
 * SSR-safe: All access is safe for server-side rendering.
 */

// Required environment variables (build will fail if missing in production)
const requiredClientEnv = [
  "VITE_SUPABASE_URL",
  "VITE_SUPABASE_ANON_KEY",
] as const;

// Optional environment variables (have safe defaults)
const optionalClientEnv = [
  "VITE_APP_VERSION",
  "VITE_BUILD_DATE",
  "VITE_SENTRY_DSN",
  "VITE_SENTRY_ENVIRONMENT",
  "VITE_SENTRY_RELEASE",
  "VITE_SENTRY_TRACES_SAMPLE_RATE",
  "VITE_ANALYTICS_ENABLED",
  "VITE_ANALYTICS_PROVIDER",
  "VITE_PLAUSIBLE_DOMAIN",
  "VITE_UMAMI_WEBSITE_ID",
  "VITE_GA4_MEASUREMENT_ID",
  "VITE_CUSTOM_ANALYTICS_ENDPOINT",
  "VITE_MAPBOX_TOKEN",
  "VITE_FCM_API_KEY",
  "VITE_FCM_PROJECT_ID",
  "VITE_STREAM_API_KEY",
] as const;

export type RequiredEnvKey = typeof requiredClientEnv[number];
export type OptionalEnvKey = typeof optionalClientEnv[number];
export type EnvKey = RequiredEnvKey | OptionalEnvKey;

/**
 * Get all environment variables (safe access)
 * Returns a record of all env keys with their values (or undefined)
 */
export function getEnv(): Record<string, string | undefined> {
  const env: Record<string, string | undefined> = {};
  
  [...requiredClientEnv, ...optionalClientEnv].forEach((key) => {
    env[key] = import.meta.env[key];
  });
  
  return env;
}

/**
 * Get environment variable value with safe fallback
 */
export function getEnvValue(key: EnvKey, defaultValue?: string): string | undefined {
  return import.meta.env[key] ?? defaultValue;
}

/**
 * Check if an environment variable is present
 */
export function hasEnv(key: EnvKey): boolean {
  const value = import.meta.env[key];
  return !!value && value !== "";
}

/**
 * Assert that all required environment variables are present
 * Throws an error in production if any required keys are missing
 * 
 * This should be called before app initialization in production builds
 */
export function assertRequiredEnv(): void {
  const missing = requiredClientEnv.filter(
    (key) => !import.meta.env[key] || import.meta.env[key] === ""
  );

  if (missing.length > 0) {
    throw new Error(
      `[ChurchHub] Missing required environment variables: ${missing.join(", ")}`
    );
  }
}

/**
 * Get environment variable presence map (booleans only - no secrets)
 * Useful for debugging and health checks
 */
export function getEnvPresence(): Record<string, boolean> {
  const env = getEnv();
  const result: Record<string, boolean> = {};
  
  for (const key in env) {
    result[key] = hasEnv(key as EnvKey);
  }
  
  return result;
}

/**
 * Get list of required environment variable names
 */
export function getRequiredEnvKeys(): readonly string[] {
  return requiredClientEnv;
}

/**
 * Get list of optional environment variable names
 */
export function getOptionalEnvKeys(): readonly string[] {
  return optionalClientEnv;
}

